import { CustomSelect } from '../../Inputs/CustomSelect/CustomSelect';
import { UniversalToggle } from '@practium/ui';
import { CULTURE_LABELS, CROP_STAGE_LABELS, CULTURES, CROP_STAGES } from '../../../constants';
import type { Culture, CropStage } from '../../../constants';
import styles from './ParcellingFormSection.module.css';

const SelectArrow = () => (
	<svg
		className={styles.selectArrow}
		width="11"
		height="7"
		viewBox="0 0 12 8"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M1 1.5L6 6.5L11 1.5"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface ParcellingFormSectionProps {
	name: string;
	culture: Culture;
	cropStage: CropStage;
	isPublic: boolean;
	note?: string;
	bindingUserDisplayName?: string;
	selectedFarmName?: string;
	canBindUser?: boolean;
	canBindFarm?: boolean;
	canManagePublicVisibility: boolean;
	onNameChange?: (value: string) => void;
	onCultureChange?: (value: Culture) => void;
	onCropStageChange?: (value: CropStage) => void;
	onIsPublicChange?: (value: boolean) => void;
	onNoteChange?: (value: string) => void;
	onOpenClientModal?: () => void;
	onOpenFarmModal?: () => void;
	isEditing?: boolean;
	errors?: Record<string, string>;
}

export const ParcellingFormSection = ({
	name,
	culture,
	cropStage,
	isPublic,
	note = '',
	bindingUserDisplayName,
	selectedFarmName,
	canBindUser = false,
	canBindFarm = false,
	canManagePublicVisibility,
	onNameChange,
	onCultureChange,
	onCropStageChange,
	onIsPublicChange,
	onNoteChange,
	onOpenClientModal,
	onOpenFarmModal,
	isEditing = false,
	errors = {},
}: ParcellingFormSectionProps) => {
	const cultureOptions = CULTURES.map((c) => ({ value: c, label: CULTURE_LABELS[c] }));
	const cropStageOptions = CROP_STAGES.map((s) => ({ value: s, label: CROP_STAGE_LABELS[s] }));

	if (!isEditing) {
		return (
			<div className={styles.grid}>
				<div className={styles.field}>
					<span className={styles.label}>Identificação</span>
					<span className={styles.value}>{name || '-'}</span>
				</div>
				<div className={styles.field}>
					<span className={styles.label}>Cultura</span>
					<span className={styles.value}>{CULTURE_LABELS[culture]}</span>
				</div>
				<div className={styles.field}>
					<span className={styles.label}>Estágio da Cultura</span>
					<span className={styles.value}>{CROP_STAGE_LABELS[cropStage]}</span>
				</div>
				{canBindUser && (
					<div className={styles.field}>
						<span className={styles.label}>Clientes</span>
						<span className={styles.value}>{bindingUserDisplayName || 'Todos os Clientes'}</span>
					</div>
				)}
				{canBindFarm && (
					<div className={styles.field}>
						<span className={styles.label}>Fazendas</span>
						<span className={styles.value}>{selectedFarmName || 'Todas as Fazendas'}</span>
					</div>
				)}
				{canManagePublicVisibility && (
					<div className={styles.field}>
						<span className={styles.label}>Visibilidade Pública</span>
						<span className={styles.value}>{isPublic ? 'Sim' : 'Não'}</span>
					</div>
				)}
				<div className={`${styles.field} ${styles.fieldFullWidth}`}>
					<span className={styles.label}>Observação</span>
					<span className={styles.value}>{note || '-'}</span>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.grid}>
			<div className={styles.field}>
				<p className={`${styles.label} ${errors.name ? styles.labelError : ''}`}>
					Identificação
				</p>
				<input
					type="text"
					value={name}
					onChange={(e) => onNameChange?.(e.target.value)}
					placeholder="Nome do parcelamento"
					className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
				/>
			</div>

			<div className={styles.field}>
				<p className={`${styles.label} ${errors.culture ? styles.labelError : ''}`}>
					Cultura
				</p>
				<CustomSelect
					value={culture}
					onChange={(value) => onCultureChange?.(value as Culture)}
					options={cultureOptions}
					placeholder="Selecionar..."
					error={errors.culture}
				/>
			</div>

			<div className={styles.field}>
				<p className={`${styles.label} ${errors.cropStage ? styles.labelError : ''}`}>
					Estágio da Cultura
				</p>
				<CustomSelect
					value={cropStage}
					onChange={(value) => onCropStageChange?.(value as CropStage)}
					options={cropStageOptions}
					placeholder="Selecionar..."
					error={errors.cropStage}
				/>
			</div>

			{canBindUser && (
				<div className={styles.field}>
					<p className={`${styles.label} ${errors.bindingUserIds ? styles.labelError : ''}`}>
						Cliente
					</p>
					<button
						type="button"
						className={`${styles.selectTrigger} ${errors.bindingUserIds ? styles.inputError : ''}`}
						onClick={onOpenClientModal}
					>
						<span
							className={
								bindingUserDisplayName ? styles.selectTriggerValue : styles.selectTriggerPlaceholder
							}
						>
							{bindingUserDisplayName || 'Selecionar...'}
						</span>
						<SelectArrow />
					</button>
				</div>
			)}

			{canBindFarm && (
				<div className={styles.field}>
					<p className={styles.label}>Fazenda</p>
					<button type="button" className={styles.selectTrigger} onClick={onOpenFarmModal}>
						<span
							className={
								selectedFarmName ? styles.selectTriggerValue : styles.selectTriggerPlaceholder
							}
						>
							{selectedFarmName || 'Selecionar...'}
						</span>
						<SelectArrow />
					</button>
				</div>
			)}

			{canManagePublicVisibility && (
				<div className={styles.fieldToggle}>
					<span className={styles.label}>Visibilidade Pública</span>
					<UniversalToggle
						checked={isPublic}
						onChangeToggle={(checked) => onIsPublicChange?.(checked)}
					/>
				</div>
			)}

			<div className={styles.field}>
				<p className={styles.label}>Observação</p>
				<textarea
					value={note}
					onChange={(e) => onNoteChange?.(e.target.value)}
					placeholder="Clique e adicione a observação aqui"
					className={styles.textarea}
					rows={3}
				/>
			</div>
		</div>
	);
};
